import { useEffect, useState } from "react";
import { BadgeCheck, Crown, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, shortDate } from "../lib/api";

const STATUS_CLASS = { pending: "sent", activated: "paid", downgraded: "overdue" };

export default function UpgradeRequestsView() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activatingId, setActivatingId] = useState(null);

  const load = () =>
    api.get("/admin/upgrade_requests")
      .then(({ data }) => setRequests(data))
      .catch((error) => toast.error(error.response?.data?.detail || "Could not load upgrade requests"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const activate = async (req) => {
    if (!window.confirm(`Activate Pro for ${req.business_name || req.email}?`)) return;
    setActivatingId(req.id);
    try {
      await api.post("/auth/upgrade", { user_id: req.user_id, plan: "pro" });
      toast.success(`Pro activated for ${req.business_name || req.email}`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not activate Pro");
    } finally {
      setActivatingId(null);
    }
  };

  const pending = requests.filter((r) => r.status === "pending");

  return (
    <section className="panel module-panel" data-testid="upgrade-requests-view">
      <div className="module-toolbar">
        <div className="table-search">
          <Crown size={16} />
          <span className="table-search-hint">{loading ? "Loading…" : `${pending.length} pending · ${requests.length} total`}</span>
        </div>
        <button className="secondary-button" data-testid="upgrade-refresh-button" onClick={load}><RefreshCw size={14} /> Refresh</button>
      </div>
      <div className="table-wrap">
        {loading ? (
          <div className="admin-loading">Loading upgrade requests…</div>
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><Crown size={22} /></div>
            <h3>No upgrade requests yet</h3>
            <p>When a Starter client requests Pro, it will appear here (and you'll get an email).</p>
          </div>
        ) : (
          <table data-testid="upgrade-requests-table">
            <thead>
              <tr><th>Business</th><th>Account email</th><th>Requested</th><th>Source</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id} data-testid={`upgrade-row-${req.id}`}>
                  <td><strong>{req.business_name || "—"}</strong></td>
                  <td>{req.email}</td>
                  <td>{shortDate(req.created_at)}</td>
                  <td><span className="category-tag">{req.source === "signup" ? "Signup" : "In-app"}</span></td>
                  <td><span className={`status ${STATUS_CLASS[req.status] || "sent"}`}>{req.status === "activated" ? "Activated" : req.status === "downgraded" ? "Downgraded" : "Pending"}</span></td>
                  <td>
                    {req.status === "pending" ? (
                      <button className="link-button" data-testid={`activate-pro-${req.id}`} disabled={activatingId === req.id} onClick={() => activate(req)}>
                        <BadgeCheck size={14} /> {activatingId === req.id ? "Activating…" : "Activate Pro"}
                      </button>
                    ) : <span className="report-note" style={{ margin: 0 }}>Resolved</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
